<template>
  <div class="w-full h-full">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <div class="animate-pulse text-gray-400">Loading chart...</div>
    </div>

    <div
      v-else-if="chartData.length === 0"
      class="flex items-center justify-center h-full"
    >
      <div class="text-gray-400">No data available</div>
    </div>

    <canvas v-else ref="chartCanvas" class="w-full h-full"></canvas>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from "vue";
import {
  Chart,
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartConfiguration,
} from "chart.js";

// Register all Chart.js components globally
Chart.register(
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ChartDataPoint {
  date: string;
  sparks_spent: number;
}

interface Props {
  data: ChartDataPoint[];
  period: number;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const chartCanvas = ref<HTMLCanvasElement | null>(null);
let chartInstance: Chart<"line"> | null = null;

const chartData = computed(() => props.data || []);

function createChart() {
  if (!chartCanvas.value || props.loading || chartData.value.length === 0)
    return;

  const ctx = chartCanvas.value.getContext("2d");
  if (!ctx) return;

  // Destroy existing chart
  if (chartInstance) {
    chartInstance.destroy();
  }

  const labels = chartData.value.map((d) => {
    const date = new Date(d.date);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  });

  const sparkData = chartData.value.map((d) => d.sparks_spent);

  const config: ChartConfiguration<"line"> = {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "Sparks Spent",
          data: sparkData,
          borderColor: "#FACC15", // Spark yellow
          backgroundColor: "rgba(250, 204, 21, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: "#FACC15",
          pointBorderColor: "#FACC15",
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          borderColor: "#FACC15",
          borderWidth: 1,
          callbacks: {
            label: function (context) {
              return `Sparks: ${context.parsed.y.toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 12,
            },
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          grid: {
            color: "rgba(107, 114, 128, 0.1)",
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 12,
            },
            callback: function (value) {
              return typeof value === "number" ? value.toLocaleString() : value;
            },
          },
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      elements: {
        point: {
          hoverRadius: 8,
        },
      },
    },
  };

  chartInstance = new Chart(ctx, config);
}

// Watch for data changes
watch(
  () => [props.data, props.loading],
  async () => {
    await nextTick();
    createChart();
  },
  { deep: true }
);

onMounted(async () => {
  await nextTick();
  createChart();
});

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }
});
</script>

<style scoped>
canvas {
  max-height: 100%;
}
</style>
